import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, ZoomIn, ZoomOut, RotateCcw, X } from "lucide-react";
import heroBackground from "@/assets/hero-background-biochemistry.webp";
import ebookCover from "@/assets/ebook-seelenbalance.webp";
import ebookBackcover from "@/assets/ebook-backcover.jpg.asset.json";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { useT, useLang } from "@/i18n";

const EBook = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [enlargedImage, setEnlargedImage] = useState<{ src: string; alt: string } | null>(null);
  const [zoom, setZoom] = useState(1);
  const lang = useLang();
  const t = useT(
    {
      seoTitle: "Seelenbalance – Buch von Isabella Bartak | PRAXIS IB",
      seoDesc:
        "„Seelenbalance – Wie überlebe ich meine Genesung?“ Ein ehrlicher Erfahrungsbericht über chronische Erkrankung, Genesung und mentale Stärke. Leseprobe verfügbar.",
      bookDesc: "Ein ehrlicher Erfahrungsbericht über chronische Erkrankung, Genesung und mentale Stärke.",
      h1: "Mein Buch",
      subtitle: "Seelenbalance – Wie überlebe ich meine Genesung??",
      coverAlt: "Seelenbalance – Wie überlebe ich meine Genesung??",
      backcoverAlt: "Seelenbalance – Rückseite mit Buchbeschreibung",
      enlarge: "Bild vergrößern",
      available: "Jetzt erhältlich",
      from: "ab",
      zoomOut: "Verkleinern",
      zoomIn: "Vergrößern",
      zoomReset: "Zoom zurücksetzen",
      close: "Schließen",
      showCover: (n: number) => `Cover ${n} anzeigen`,
      front: "Vorderseite",
      back: "Rückseite",
      flipHint: "Pfeile zum Blättern",
      amazonTitle: "Jetzt auf Amazon erhältlich",
      amazonText:
        "„Seelenbalance“ ist ab sofort als Kindle E-Book und gedrucktes Taschenbuch auf Amazon verfügbar.",
      amazonCta: "Jetzt auf Amazon bestellen",
      amazonNote: "Sie werden zu Amazon.de weitergeleitet",
      descTitle: "Seelenbalance",
      descSubtitle: "Wie überlebe ich meine Genesung??",
      p1: "In meiner Praxis konzentriere ich mich auf die körperliche Ebene und betrachte den Körper als ein komplexes, vernetztes System. Als ehemalige Patientin weiß ich jedoch, dass der Weg zur Gesundheit nicht nur eine körperliche Herausforderung ist. Oft ist es psychisch und mental eine enorme Belastung, diesen Prozess durchzuhalten – ganz gleich, ob man bereits auf dem Weg ist oder noch sucht. Es geht nicht nur darum, gesund zu werden, sondern auch darum, wie man diesen Weg bis dorthin übersteht.",
      p2: "Denn wer in der Dunkelheit lebt, muss lernen, das Licht zu sehen. Ich spreche aus eigener Erfahrung – ich kenne die Tiefen, die Stille und die Momente, in denen die Hoffnung schwindet. Doch genau dort, im scheinbar Unendlichen, entdecken wir eine verborgene Wahrheit: Die Fähigkeit, Licht zu erkennen, ist keine Gabe, sondern eine Kompetenz, die wir uns aneignen können, um durchzuhalten.",
      p3: "Dieses Buch ist kein klassischer Ratgeber, sondern ein Wegweiser für die Momente, in denen wir uns allein fühlen. Es soll ein stiller Begleiter sein, der uns an die Hand nimmt, wenn der Weg nicht mehr sichtbar scheint.",
      p4: "Sie sind nicht allein. Und ja – es ist möglich.",
      quote: "„Jeder Tag ist ein kleines Stückchen Leben.“",
      note: "Das Buch ist derzeit ausschließlich auf Deutsch erhältlich.",
    },
    {
      seoTitle: "Seelenbalance – Book by Isabella Bartak | PRAXIS IB",
      seoDesc:
        "“Seelenbalance – How do I survive my recovery?” An honest personal account of chronic illness, recovery and mental resilience. Reading sample available.",
      bookDesc: "An honest personal account of chronic illness, recovery and mental resilience.",
      h1: "My Book",
      subtitle: "Seelenbalance – How do I survive my recovery??",
      coverAlt: "Seelenbalance – How do I survive my recovery??",
      backcoverAlt: "Seelenbalance – back cover with book description",
      enlarge: "Enlarge image",
      available: "Available now",
      from: "from",
      zoomOut: "Zoom out",
      zoomIn: "Zoom in",
      zoomReset: "Reset zoom",
      close: "Close",
      showCover: (n: number) => `Show cover ${n}`,
      front: "Front cover",
      back: "Back cover",
      flipHint: "use the arrows to browse",
      amazonTitle: "Available now on Amazon",
      amazonText:
        "“Seelenbalance” is available now as a Kindle e-book and as a printed paperback on Amazon.",
      amazonCta: "Order now on Amazon",
      amazonNote: "You will be redirected to Amazon.de",
      descTitle: "Seelenbalance",
      descSubtitle: "How do I survive my recovery??",
      p1: "In my practice I focus on the physical level and view the body as a complex, interconnected system. As a former patient, however, I know that the path to health is not only a physical challenge. Psychologically and mentally it is often an enormous strain to keep going through this process – whether you are already on your way or still searching. It is not only about getting well, but also about how you get through the journey there.",
      p2: "Anyone who lives in darkness has to learn to see the light. I speak from my own experience – I know the depths, the silence and the moments when hope fades. Yet precisely there, in what seems endless, we discover a hidden truth: the ability to recognise light is not a gift but a skill we can learn in order to keep going.",
      p3: "This book is not a conventional self-help guide but a compass for those moments when we feel alone. It is meant to be a quiet companion that takes us by the hand when the path no longer seems visible.",
      p4: "You are not alone. And yes – it is possible.",
      quote: "“Every day is a small piece of life.”",
      note: "The book is currently available in German only.",
    },
  );

  useSeo({
    title: t.seoTitle,
    description: t.seoDesc,
    canonical: "/e-book",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Book",
      name: "Seelenbalance – Wie überlebe ich meine Genesung?",
      author: { "@type": "Person", name: "Isabella Bartak" },
      inLanguage: "de",
      workTranslation: undefined,
      bookFormat: "https://schema.org/EBook",
      url: absoluteUrl("/e-book"),
      description: t.bookDesc,
    },
    jsonLdId: "ebook-jsonld",
  });

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);



  const covers = [
    {
      src: ebookCover,
      alt: t.coverAlt,
      showBadges: true,
    },
    {
      src: ebookBackcover.url,
      alt: t.backcoverAlt,
      showBadges: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{ backgroundImage: `url(${heroBackground})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t.h1}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              {t.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* E-Book Content */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col gap-12 items-center max-w-2xl mx-auto">
              {/* Book Cover Carousel */}
              <div className="flex flex-col items-center gap-3">
                <div className="w-full max-w-sm">
                  <Carousel setApi={setApi} className="w-full">
                    <CarouselContent>
                      {covers.map((cover, idx) => (
                        <CarouselItem key={idx}>
                          <div
                            className="group relative block rounded-2xl overflow-hidden shadow-2xl border border-border transition-transform hover:scale-[1.02] cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                            role="button"
                            tabIndex={0}
                            aria-label={t.enlarge}
                            onClick={() => setEnlargedImage({ src: cover.src, alt: cover.alt })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setEnlargedImage({ src: cover.src, alt: cover.alt });
                              }
                            }}
                          >
                            <img
                              src={cover.src}
                              alt={cover.alt}
                              className="w-full h-auto"
                            />
                            {cover.showBadges && (
                              <>
                                <span className="absolute top-4 right-4 z-10 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                                  {t.available}
                                </span>
                                <span className="absolute bottom-4 left-4 z-10 bg-background/95 text-foreground border border-primary/30 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                                  <span className="text-[0.4rem] font-normal mr-0.5 align-middle">{t.from}</span>9,99 €
                                </span>
                              </>
                            )}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/90 via-background/60 to-transparent px-4 py-3 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                                <ZoomIn className="w-4 h-4" />
                                {t.enlarge}
                              </span>
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-2 bg-background/90 border-primary/30" />
                    <CarouselNext className="right-2 bg-background/90 border-primary/30" />
                  </Carousel>

                  <Dialog
                    open={!!enlargedImage}
                    onOpenChange={(open) => {
                      if (!open) {
                        setEnlargedImage(null);
                        setZoom(1);
                      }
                    }}
                  >
                    <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-lg p-3 sm:p-4 max-h-[90vh] flex flex-col gap-3">
                      <DialogHeader>
                        <DialogTitle className="text-sm font-medium pr-8 truncate">
                          {enlargedImage?.alt}
                        </DialogTitle>
                      </DialogHeader>
                      {enlargedImage && (
                        <div className="overflow-auto rounded-lg bg-muted/30 flex-1 flex items-start justify-center">
                          <img
                            src={enlargedImage.src}
                            alt={enlargedImage.alt}
                            style={{
                              transform: `scale(${zoom})`,
                              transformOrigin: "top center",
                              transition: "transform 0.15s ease-out",
                            }}
                            className="w-full h-auto max-w-full rounded-lg"
                          />
                        </div>
                      )}
                      <DialogFooter className="flex-row justify-center items-center gap-2 sm:justify-center mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={t.zoomOut}
                          onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                          disabled={zoom <= 0.5}
                        >
                          <ZoomOut className="w-4 h-4" />
                        </Button>
                        <span className="text-sm tabular-nums w-12 text-center text-muted-foreground">
                          {Math.round(zoom * 100)}%
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={t.zoomIn}
                          onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
                          disabled={zoom >= 3}
                        >
                          <ZoomIn className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          aria-label={t.zoomReset}
                          onClick={() => setZoom(1)}
                          disabled={zoom === 1}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="default"
                          onClick={() => {
                            setEnlargedImage(null);
                            setZoom(1);
                          }}
                          className="ml-2"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t.close}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>


                  {/* Dot Indicators */}
                  <div className="flex justify-center gap-2 mt-4">
                    {covers.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => api?.scrollTo(idx)}
                        aria-label={t.showCover(idx + 1)}
                        className={`h-2 rounded-full transition-all ${
                          current === idx
                            ? "w-6 bg-primary"
                            : "w-2 bg-muted-foreground/40 hover:bg-muted-foreground/60"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    {current === 0 ? t.front : t.back} · {t.flipHint}
                  </p>
                </div>
              </div>


              {/* Amazon CTA */}
              <div className="w-full">
                <div className="bg-gradient-to-br from-muted/60 to-muted/30 rounded-2xl p-6 border border-primary/20 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-5 h-5 text-primary" />
                    <span className="text-lg font-semibold text-foreground">
                      {t.amazonTitle}
                    </span>
                  </div>
                  <p className="text-muted-foreground mb-5">
                    {t.amazonText}
                  </p>
                  <a
                    href="https://www.amazon.de/dp/B0H82R51FJ"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex w-full items-center justify-center gap-3 rounded-xl bg-[#FF9900] hover:bg-[#e88b00] text-[#131A22] font-semibold px-6 py-4 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  >
                    <svg viewBox="0 0 100 30" aria-hidden="true" className="h-6 w-auto">
                      <text x="0" y="22" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="24" fill="#131A22">amazon</text>
                      <path d="M4 25 Q 38 36 74 25" stroke="#131A22" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                      <polygon points="70,24 78,24 74,29" fill="#131A22" />
                    </svg>
                    <span>{t.amazonCta}</span>
                  </a>
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    {t.amazonNote}
                  </p>
                </div>
              </div>

              {/* Book Description */}
              <div className="space-y-6 w-full">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                  {t.descTitle}
                </h2>
                <p className="text-xl text-primary font-medium">
                  {t.descSubtitle}
                </p>
                

                
                <div className="prose prose-lg text-muted-foreground space-y-4">
                  <p>{t.p1}</p>
                  <p>{t.p2}</p>
                  <p>{t.p3}</p>
                  <p className="font-medium text-foreground">{t.p4}</p>
                </div>

                {/* Quote from book */}
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                  {t.quote}
                </blockquote>
                {lang === "en" && (
                  <p className="text-sm text-muted-foreground">{t.note}</p>
                )}

                <p className="text-sm text-muted-foreground">
                  © Isabella Bartak, 2026
                </p>



              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EBook;
